import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronDownIcon } from '@chewy/kib-icons-react'
import { KibSpinningLoader } from '@chewy/kib-overlays-react/legacy'
import classNames from 'classnames'

import type { AgentChatMessage } from '~/api/agentChat'

import ChatMessage from './ChatMessage'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'
import styles from './ChatMessagesView.module.scss'

export interface ChatMessagesViewProps {
  isLoading?: boolean
  messages: AgentChatMessage[]
  sessionId: string | null
}

const ChatMessagesView: React.FC<ChatMessagesViewProps> = ({
  messages,
  isLoading = false,
  sessionId,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesListRef = useRef<HTMLDivElement>(null)
  const isInitialMount = useRef(true)
  const previousMessagesLengthRef = useRef<number>(0)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const { t } = useTranslation('Agent')

  const scrollToBottom = () => {
    const behavior = isInitialMount.current ? 'auto' : 'smooth'
    messagesEndRef.current?.scrollIntoView({ behavior })
    isInitialMount.current = false
  }

  const handleScroll = () => {
    if (!messagesListRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100

    setShowScrollToBottom(!isNearBottom)
  }

  useEffect(() => {
    previousMessagesLengthRef.current = 0
    isInitialMount.current = true
  }, [sessionId])

  useEffect(() => {
    const currentLength = messages.length
    const previousLength = previousMessagesLengthRef.current
    const hasNewMessage = currentLength > previousLength

    let isNewUserMessage = false
    if (hasNewMessage) {
      const newlyAddedMessages = messages.slice(previousLength)
      isNewUserMessage = newlyAddedMessages.some(
        (message) => message.sender === 'user',
      )
    }

    // Scroll to bottom on initial mount or when a new user message is added
    if (isInitialMount.current || isNewUserMessage) {
      scrollToBottom()
    }

    previousMessagesLengthRef.current = currentLength
  }, [messages])

  useEffect(() => {
    const messagesList = messagesListRef.current
    if (!messagesList) return () => {}

    messagesList.addEventListener('scroll', handleScroll)

    const resizeObserver = new ResizeObserver(() => {
      handleScroll()
    })
    resizeObserver.observe(messagesList)

    return () => {
      messagesList.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [])

  const mostRecentAgentMessageId = React.useMemo(() => {
    // Don't mark any message as most recent if the last message is loading
    // (agent is still responding)
    if (messages.length > 0 && messages[messages.length - 1].isLoading) {
      return null
    }

    // Find the most recent non-loading agent message
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (message.sender === 'agent' && !message.isLoading) {
        return message.id
      }
    }
    return null
  }, [messages])

  return (
    <div className={styles.messagesContainer}>
      <div className={styles.messagesList} ref={messagesListRef}>
        <>
          {messages.map((message) => (
            <ChatMessage
              isMostRecent={message.id === mostRecentAgentMessageId}
              key={message.id}
              message={message}
              sessionId={sessionId}
            />
          ))}
          <div ref={messagesEndRef} />
        </>
      </div>
      {isLoading && <KibSpinningLoader embedded />}
      <KibButtonNew
        aria-label={t('Agent:SCROLL_TO_BOTTOM')}
        className={classNames(styles.scrollToBottomButton, {
          [styles.visible]: showScrollToBottom,
          [styles.hidden]: !showScrollToBottom,
        })}
        icon={<KibChevronDownIcon />}
        size="small"
        theme="utility"
        onClick={scrollToBottom}
      />
    </div>
  )
}

export default ChatMessagesView
